import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Heart, GitCompare, Maximize2, DoorOpen, MapPin, ArrowRight } from "lucide-react";
import { formatPrezzo } from "@/data/properties";
import { fileUrl } from "@/lib/api";
import { useApp } from "@/context/AppState";

const LABEL_STYLES = {
  "Novità": "bg-brand text-white",
  "In esclusiva": "bg-anthracite text-white",
  "Nuova costruzione": "bg-white text-brand border border-brand",
};

export default function PropertyCard({ property, view = "grid", index = 0 }) {
  const { favorites, toggleFavorite, compare, toggleCompare } = useApp();
  const isFav = favorites.includes(property.id);
  const isCompare = compare.includes(property.id);
  const isList = view === "list";

  return (
    <motion.article
      initial={{ opacity: 0, y: 24 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-40px" }}
      transition={{ duration: 0.6, delay: (index % 3) * 0.08, ease: [0.22, 1, 0.36, 1] }}
      className={`group bg-white rounded-3xl border border-border-subtle overflow-hidden shadow-[0_10px_40px_-24px_rgba(15,92,74,0.35)] hover:shadow-[0_24px_60px_-24px_rgba(15,92,74,0.45)] transition-shadow ${
        isList ? "sm:flex" : ""
      }`}
      data-testid={`property-card-${property.id}`}
    >
      <div className={`relative overflow-hidden ${isList ? "sm:w-72 lg:w-96 shrink-0" : ""}`}>
        <Link to={`/immobili/${property.id}`} className="block">
          <img
            src={fileUrl(property.immagini[0])}
            alt={property.titolo}
            className={`w-full object-cover transition-transform duration-700 group-hover:scale-105 ${
              isList ? "h-56 sm:h-full" : "h-60"
            }`}
          />
        </Link>
        <div className="absolute top-3 left-3 flex gap-2">
          <span className="rounded-full bg-white/90 backdrop-blur text-anthracite text-xs font-semibold px-3 py-1 capitalize">
            {property.transazione}
          </span>
          {property.etichetta && (
            <span className={`rounded-full text-xs font-semibold px-3 py-1 ${LABEL_STYLES[property.etichetta] || "bg-brand text-white"}`}>
              {property.etichetta}
            </span>
          )}
        </div>
        <div className="absolute top-3 right-3 flex gap-2">
          <button
            onClick={() => toggleFavorite(property.id)}
            aria-label="Aggiungi ai preferiti"
            className="grid place-items-center h-9 w-9 rounded-full bg-white/90 backdrop-blur hover:bg-white transition-colors"
            data-testid={`fav-btn-${property.id}`}
          >
            <Heart className={`h-4 w-4 ${isFav ? "fill-brand text-brand" : "text-anthracite"}`} />
          </button>
          <button
            onClick={() => toggleCompare(property.id)}
            aria-label="Confronta immobile"
            className={`grid place-items-center h-9 w-9 rounded-full backdrop-blur transition-colors ${
              isCompare ? "bg-brand text-white" : "bg-white/90 text-anthracite hover:bg-white"
            }`}
            data-testid={`compare-btn-${property.id}`}
          >
            <GitCompare className="h-4 w-4" />
          </button>
        </div>
      </div>

      <div className="p-5 flex flex-col flex-1">
        <div className="flex items-start justify-between gap-3">
          <p className="font-display text-2xl font-semibold text-brand leading-none">{formatPrezzo(property)}</p>
          <span className="text-[11px] font-semibold text-anthracite/60 border border-border-subtle rounded-full px-2 py-0.5">
            Classe {property.classeEnergetica}
          </span>
        </div>
        <h3 className="mt-2 font-semibold text-anthracite text-lg leading-tight">{property.titolo}</h3>
        <p className="mt-1 flex items-center gap-1.5 text-sm text-anthracite/60">
          <MapPin className="h-3.5 w-3.5 text-brand" /> {property.comune} — {property.zona}
        </p>

        <div className="mt-4 flex items-center gap-4 text-sm text-anthracite/70">
          <span className="inline-flex items-center gap-1.5"><Maximize2 className="h-4 w-4 text-brand" /> {property.superficie} mq</span>
          <span className="inline-flex items-center gap-1.5"><DoorOpen className="h-4 w-4 text-brand" /> {property.locali} locali</span>
        </div>

        <p className="mt-3 text-sm text-brand font-medium bg-accent-subtle rounded-lg px-3 py-2">
          {property.caratteristica}
        </p>

        <Link
          to={`/immobili/${property.id}`}
          className="group/btn mt-4 inline-flex items-center justify-center gap-2 rounded-full bg-brand text-white font-semibold py-3 transition-all hover:bg-brand-hover"
          data-testid={`discover-btn-${property.id}`}
        >
          Scopri l'immobile
          <ArrowRight className="h-4 w-4 transition-transform group-hover/btn:translate-x-1" />
        </Link>
      </div>
    </motion.article>
  );
}
